import type { Dayjs } from 'dayjs';

import { memo, useCallback, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Menu, MenuItem, Typography } from '@mui/material';

import { Iconify } from 'src/components/iconify';

import {
  stepHeaderMenuItemSx,
  stepHeaderPillLabelSx,
  stepHeaderPillSx,
} from './step-header-pill-styles';
import { groupSelectedDatesIntoRanges } from '../group-selected-dates';

interface SelectedDatesDropdownPillProps {
  selectedDates: Dayjs[];
}

export const SelectedDatesDropdownPill = memo(({ selectedDates }: SelectedDatesDropdownPillProps) => {
  const { t } = useTranslation('shuffle');
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const options = useMemo(() => groupSelectedDatesIntoRanges(selectedDates), [selectedDates]);

  const isOpen = Boolean(anchorEl);
  const displayOption = options[0] ?? null;

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (options.length <= 1) {
        return;
      }
      setAnchorEl(event.currentTarget);
    },
    [options.length]
  );

  const handleClose = useCallback((_event: object, reason?: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      setAnchorEl(null);
    }
  }, []);

  const handleMenuItemClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  if (!displayOption) {
    return null;
  }

  const showChevron = options.length > 1;

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-haspopup={showChevron ? 'listbox' : undefined}
        aria-expanded={showChevron ? isOpen : undefined}
        aria-controls={showChevron && isOpen ? menuId : undefined}
        aria-label={t('selectedDatesDropdown.ariaLabel')}
        onClick={handleOpen}
        disabled={!showChevron}
        sx={{
          ...stepHeaderPillSx(isOpen),
          cursor: showChevron ? 'pointer' : 'default',
          m: 0,
          padding: (theme) => theme.spacing(0, 1),
          font: 'inherit',
          textAlign: 'left',
          appearance: 'none',
          WebkitAppearance: 'none',
        }}
      >
        <Typography component="span" sx={stepHeaderPillLabelSx}>
          {displayOption.label}
        </Typography>
        {showChevron ? (
          <Iconify icon="eva:arrow-ios-downward-fill" width={24} sx={{ color: 'text.primary' }} />
        ) : null}
      </Box>

      {showChevron ? (
        <Menu
          id={menuId}
          anchorEl={anchorEl}
          open={isOpen}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                mt: 0.5,
                minWidth: anchorEl?.offsetWidth ?? 160,
              },
            },
          }}
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={handleMenuItemClick}
              sx={{
                ...stepHeaderMenuItemSx,
                cursor: 'default',
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </>
  );
});

SelectedDatesDropdownPill.displayName = 'SelectedDatesDropdownPill';
